"use client";

import { auth } from "@/config/firebaseApp";
import { onAuthStateChanged, type User } from "firebase/auth";
import { errorHandler } from "@/config/errorReporting";

let sesionResuelta: Promise<User | null> | null = null;

/**
 * Espera a que Firebase termine de restaurar la sesión.
 *
 * `auth.currentUser` es `null` durante los primeros cientos de milisegundos
 * después de cargar la página: el SDK todavía está leyendo la sesión de
 * IndexedDB. Quien llamara en esa ventana mandaba el pedido sin encabezado y
 * se comía un 401, o —peor— quedaba gateado esperando un token que llegaba
 * después de que la consulta ya se había decidido a no correr.
 *
 * `onAuthStateChanged` avisa una sola vez que la restauración terminó, con el
 * usuario o con `null` si no había sesión. La promesa se memoriza mientras
 * dura la espera —para que veinte pedidos simultáneos no abran veinte
 * suscripciones— y se suelta al resolverse: así un cierre de sesión posterior
 * no queda servido desde una respuesta vieja.
 *
 * La espera tiene tope. Sin él, un `onAuthStateChanged` que no dispare nunca
 * —IndexedDB bloqueado, storage restringido, una falla de inicialización que
 * no cae en el callback de error— dejaría esperando para siempre a todo lo que
 * pida el token, sin error y sin log. Antes de esto el token se leía de forma
 * sincrónica: salía un `Bearer undefined` y el backend contestaba 401. Feo,
 * pero visible. El tope devuelve ese comportamiento en vez de colgarse.
 *
 * Cinco segundos porque la restauración lee IndexedDB local, sin red: en
 * condiciones normales tarda menos de medio segundo. El tope sólo salta cuando
 * algo está roto de verdad, y cuando salta queda registrado.
 */
const ESPERA_MAXIMA_MS = 5_000;

function esperarSesion(): Promise<User | null> {
	if (auth.currentUser) return Promise.resolve(auth.currentUser);
	if (!sesionResuelta) {
		sesionResuelta = new Promise<User | null>((resolve) => {
			const terminar = (usuario: User | null) => {
				clearTimeout(reloj);
				desuscribir();
				sesionResuelta = null;
				resolve(usuario);
			};

			const reloj = setTimeout(() => {
				errorHandler?.report(
					`Firebase no resolvió el estado de sesión en ${ESPERA_MAXIMA_MS} ms. ` +
						"El pedido sale sin encabezado de autorización y el backend va a contestar 401.",
				);
				terminar(null);
			}, ESPERA_MAXIMA_MS);

			const desuscribir = onAuthStateChanged(
				auth,
				(usuario) => terminar(usuario),
				() => terminar(null),
			);
		});
	}
	return sesionResuelta;
}

/**
 * `fetch` con el token de Firebase pedido en el momento.
 *
 * Los hooks del navegador guardaban el token en una variable —de
 * `useAuthIdToken` o de la cookie— y lo reusaban en cada llamada. Un token de
 * Firebase dura una hora: el SDK lo renueva solo unos minutos antes de que
 * venza, pero con la pestaña de fondo el navegador frena los timers, la
 * renovación se atrasa y esa copia queda vencida. Todo lo que la usaba
 * empezaba a volver 401.
 *
 * En la jornada del 7 de septiembre fueron 667 rechazos, y la proporción
 * crecía con el turno: 5,6 % de los pedidos a las 03h y 23 % a las 10h, sobre
 * el poll de estado del enfermero que corre cada 30 segundos.
 *
 * Acá el token se pide en el momento de la llamada. `getIdToken()` sin forzar
 * ya devuelve uno válido y lo renueva si hace falta, así que con esto solo
 * alcanza en la enorme mayoría de los casos. El reintento con `getIdToken(true)`
 * queda como red: si el backend igual dijo 401, se fuerza la renovación y se
 * manda una vez más.
 *
 * No reintenta más de una vez. Si el segundo intento también vuelve 401, es
 * que la sesión no sirve y quien llama tiene que enterarse.
 *
 * El reintento es seguro incluso en un POST o un PUT: el 401 lo devuelve el
 * guard de autenticación del backend, antes de ejecutar el handler, así que el
 * primer intento no alcanzó a hacer nada. No hay riesgo de duplicar el efecto.
 *
 * Sin sesión de Firebase manda el pedido igual, sin el encabezado. Es lo mismo
 * que pasaba antes —se enviaba un `Bearer undefined` y volvía 401— y se
 * mantiene a propósito: cambiar el modo de falla de un camino que ya andaba
 * así no aporta nada y agrega riesgo.
 */
export async function fetchConToken(
	url: string,
	init: RequestInit = {},
): Promise<Response> {
	const usuario = await esperarSesion();

	if (!usuario) return fetch(url, init);

	const conElToken = async (forzarRenovacion: boolean) => {
		const token = await usuario.getIdToken(forzarRenovacion);
		const headers = new Headers(init.headers);
		headers.set("Authorization", `Bearer ${token}`);
		return fetch(url, { ...init, headers });
	};

	const respuesta = await conElToken(false);

	if (respuesta.status !== 401) return respuesta;

	return conElToken(true);
}
